import type { AuthProvider } from './AuthProvider'
import { FirebaseAuthService } from './FirebaseAuthService'
import { ImpersonationSessionAuthService } from './ImpersonationSessionAuthService'
import { KeycloakAuthService } from './KeycloakAuthService'
import { config } from '@/config'
import { getKeycloakUrl } from '@/utils/domain'

/** Which provider this tab signs in with. Firebase unless something chose otherwise. */
export type AuthProviderKind = 'firebase' | 'keycloak-admin' | 'keycloak-app' | 'impersonation-session'

const SELECTION_KEY = 'scrivio.authProvider'
const KEYCLOAK_APP_EMAIL_KEY = 'scrivio.authProvider.email'

/** The realm and client each Keycloak sign-in goes through (`keycloak/realms/`). */
const KEYCLOAK_CLIENTS = {
  'keycloak-admin': { realm: 'scrivio-admin', clientId: 'scrivio-admin-portal' },
  'keycloak-app': { realm: 'scrivio', clientId: 'scrivio-app' },
} as const

type KeycloakKind = keyof typeof KEYCLOAK_CLIENTS

function isKeycloakKind(kind: string | null): kind is KeycloakKind {
  return kind !== null && kind in KEYCLOAK_CLIENTS
}

function realmAuthority(realm: string): string | null {
  const keycloakUrl = config.keycloakUrl || getKeycloakUrl()
  return keycloakUrl ? `${keycloakUrl.replace(/\/$/, '')}/realms/${realm}` : null
}

/** The admin realm's issuer in this environment, or `null` where there is no Keycloak. */
export function adminRealmAuthority(): string | null {
  return realmAuthority(KEYCLOAK_CLIENTS['keycloak-admin'].realm)
}

/** The practice realm's issuer in this environment, or `null` where there is no Keycloak. */
export function appRealmAuthority(): string | null {
  return realmAuthority(KEYCLOAK_CLIENTS['keycloak-app'].realm)
}

/**
 * The behandelaar app's choice is remembered for the browser (`localStorage`), so a new tab goes
 * straight back to Keycloak, whose session it shares; every other choice belongs to its tab.
 */
function storageFor(kind: AuthProviderKind): Storage {
  return kind === 'keycloak-app' ? localStorage : sessionStorage
}

function readSelection(): AuthProviderKind {
  const stored = sessionStorage.getItem(SELECTION_KEY) ?? localStorage.getItem(SELECTION_KEY)
  if (isKeycloakKind(stored)) return realmAuthority(KEYCLOAK_CLIENTS[stored].realm) ? stored : 'firebase'
  return stored === 'impersonation-session' ? stored : 'firebase'
}

function forgetSelection(): void {
  sessionStorage.removeItem(SELECTION_KEY)
  localStorage.removeItem(SELECTION_KEY)
  localStorage.removeItem(KEYCLOAK_APP_EMAIL_KEY)
}

function build(kind: AuthProviderKind): AuthProvider {
  if (isKeycloakKind(kind)) {
    const { realm, clientId } = KEYCLOAK_CLIENTS[kind]
    const authority = realmAuthority(realm)
    if (authority) return new KeycloakAuthService({ authority, clientId })
  }
  if (kind === 'impersonation-session') return new ImpersonationSessionAuthService()
  return new FirebaseAuthService()
}

let activeKind: AuthProviderKind = readSelection()
let active: AuthProvider = build(activeKind)

/**
 * Choose the provider this tab signs in with from here on, remembered (see {@link storageFor}) so a
 * page reload — and Keycloak's redirect back — keeps it. For `keycloak-app`, [email] is kept beside
 * it: only an address lets the sign-in page ask the backend whether the choice still holds.
 *
 * What already subscribed to the previous provider stays subscribed to it, so a caller
 * that switches signs in and then reloads the page: the admin login page's redirect to
 * Keycloak does that by itself, and the impersonation page navigates with a full load.
 */
export function selectAuthProvider(kind: AuthProviderKind, email?: string): void {
  forgetSelection()
  if (kind !== 'firebase') storageFor(kind).setItem(SELECTION_KEY, kind)
  if (kind === 'keycloak-app' && email) localStorage.setItem(KEYCLOAK_APP_EMAIL_KEY, email)
  activeKind = readSelection()
  active = build(activeKind)
}

/** The address the remembered `keycloak-app` choice was made for; `null` once it is forgotten. */
export function rememberedKeycloakAppEmail(): string | null {
  return localStorage.getItem(KEYCLOAK_APP_EMAIL_KEY)
}

/** The provider this tab signs in with — what the admin guard decides its session checks by. */
export function currentAuthProviderKind(): AuthProviderKind {
  return activeKind
}

/**
 * The provider every sign-in, sign-out and API token goes through. Firebase for everyone,
 * unless this tab chose otherwise through {@link selectAuthProvider}: the admin portal for
 * an admin switched to Keycloak, the app for a behandelaar whose organisation is, and the app
 * for an impersonation the backend issued (ADR-045 §7). Nothing selects another provider for
 * anyone still on Firebase.
 *
 * Signing out of another provider forgets the choice, so the next sign-in in the tab asks
 * the sign-in page afresh.
 */
export const authProvider: AuthProvider = new Proxy({} as AuthProvider, {
  get(_target, property) {
    const provider = active
    if (property === 'signOut' && activeKind !== 'firebase') {
      return async () => {
        // Keycloak's sign-out leaves the page, so nothing after it would run.
        forgetSelection()
        await provider.signOut()
      }
    }
    const value = Reflect.get(provider, property, provider)
    return typeof value === 'function' ? value.bind(provider) : value
  },
})
